import React, { useMemo, useState, useEffect } from 'react';
import { Button, Modal, Spin, message, Checkbox, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRamps, fetchAssignments, applyAssignment, openAssignmentModal, deleteAssignment } from './ramps-actions';
import { EditOutlined, DeleteOutlined } from '@mui/icons-material';
import {
  getRamps,
  getRampsLoading,
  getAssignments,
  getAssignmentsTotal,
  getAssignmentsLoading,
  getApplyAssignmentLoading,
} from './ramps-selector';
import SearchRamps from '../header/search/SearchRamps';
import styles from './scheduleRamps.module.scss';
import BaseGrid from '../grid/BaseGrid';

const rampTypes = ['Integration', 'New Hire', 'New Grad', 'De Novo'];

// definitions will be loaded from API (/ramps)

export default function ScheduleRamps() {
  const [nameFilter, setNameFilter] = useState('');
  const [typesFilter, setTypesFilter] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('assignment');
  const [visited, setVisited] = useState(() => ({
    definition: activeTab === 'definition',
    assignment: activeTab === 'assignment',
  }));
  const dispatch = useDispatch();
  const rampsFromStore = useSelector(getRamps) || [];
  const rampsLoading = useSelector(getRampsLoading);

  const filteredDefinitions = useMemo(() => {
    const mapped = (rampsFromStore || []).map((r: any) => ({
      key: String(r.RampId || Math.random()),
      rampName: r.Name,
      rampType: r.Type,
      hasAnyAssignments: r.HasAnyAssignments || false,
      week1: r.Week1Pattern || null,
      week2: r.Week2Pattern || null,
      week3: r.Week3Pattern || null,
      week4: r.Week4Pattern || null,
      week5: r.Week5Pattern || null,
      week6: r.Week6Pattern || null,
      week7: r.Week7Pattern || null,
      week8: r.Week8Pattern || null,
    }));

    const normalized = mapped.map((m: any) =>
      Object.entries(m).reduce((acc: any, [k, v]) => ({ ...acc, [k]: v === null || v === undefined ? '-' : v }), {})
    );

    let results = normalized;
    if (nameFilter) {
      results = results.filter((d: any) =>
        (d as any).rampName?.toString().toLowerCase().includes(nameFilter.toLowerCase())
      );
    }
    if (typesFilter && typesFilter.length) {
      results = results.filter((d: any) => typesFilter.includes((d as any).rampType));
    }

    // sort by rampName ascending, place '-' (empty) at end
    results.sort((a: any, b: any) => {
      const an = (a.rampName || '').toString().toLowerCase();
      const bn = (b.rampName || '').toString().toLowerCase();
      if (an === '-' && bn === '-') return 0;
      if (an === '-') return 1;
      if (bn === '-') return -1;
      return an.localeCompare(bn);
    });

    return results;
  }, [rampsFromStore, nameFilter, typesFilter]);

  const [defSearch, setDefSearch] = useState({ page: 0, size: 10 });
  const [assignSearch, setAssignSearch] = useState({ page: 0, size: 10 });

  const pagedDefinitions = useMemo(() => {
    const start = (defSearch.page || 0) * (defSearch.size || 10);
    return filteredDefinitions.slice(start, start + (defSearch.size || 10));
  }, [filteredDefinitions, defSearch]);

  const assignments = useSelector(getAssignments) || [];
  const assignmentsTotal = useSelector(getAssignmentsTotal) || 0;
  const assignmentsLoading = useSelector(getAssignmentsLoading);

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isApplying, setIsApplying] = useState(false);
  const applyLoading = useSelector(getApplyAssignmentLoading);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<any>(null);

  const handleConfirmNo = () => {
    if (applyLoading) return;
    setShowConfirm(false);
    setSelectedRecord(null);
  };

  const handleConfirmYes = () => {
    // Only single apply supported
    if (!selectedRecord) return;
    setIsApplying(true);
    dispatch(applyAssignment({ RampAssignmentIds: [selectedRecord.RampAssignmentId] }));
  };

  useEffect(() => {
    if (!isApplying) return;
    // wait for applyLoading to become false (saga updates reducer)
    if (!applyLoading) {
      setIsApplying(false);
      setShowConfirm(false);
      setSelectedRecord(null);
    }
  }, [applyLoading, isApplying]);

  useEffect(() => {
    // fetch assignments only when the assignment tab is active and user has visited it (clicked it)
    if (activeTab !== 'assignment' || !visited.assignment) return;
    const payload = {
      rampName: nameFilter || '',
      rampType: typesFilter && typesFilter.length ? typesFilter[0] : '',
      page: (assignSearch.page || 0) + 1,
      size: assignSearch.size || 10,
    };
    dispatch(fetchAssignments(payload));
  }, [dispatch, assignSearch.page, assignSearch.size, nameFilter, typesFilter, activeTab, visited.assignment]);

  // fetch ramp definitions only when user has clicked the Definition tab
  useEffect(() => {
    if (activeTab !== 'definition' || !visited.definition) return;
    dispatch(fetchRamps());
  }, [dispatch, activeTab, visited.definition]);

  const paginationLeft = (
    <div className={styles['paginationToggle']}>
      <Button
        type={activeTab === 'assignment' ? 'primary' : 'default'}
        htmlType='submit'
        className={`button ${activeTab === 'assignment' ? '' : 'ant-btn-default'}`}
        style={{ marginLeft: 20 }}
        onClick={() => {
          setAssignSearch((s) => ({ ...s, page: 0 }));
          setActiveTab('assignment');
          setVisited((v) => ({ ...v, assignment: true }));
        }}
      >
        Ramp Assignment
      </Button>
      <Button
        type={activeTab === 'definition' ? 'primary' : 'default'}
        htmlType='submit'
        className={`button1 ${activeTab === 'definition' ? '' : 'ant-btn-default'}`}
        onClick={() => {
          setDefSearch((s) => ({ ...s, page: 0 }));
          setActiveTab('definition');
          setVisited((v) => ({ ...v, definition: true }));
        }}
      >
        Ramp Definition
      </Button>
    </div>
  );

  const paginationRight = null;

  const definitionColumns = [
    { title: 'Ramp Name', dataIndex: 'rampName', key: 'rampName', width: 230 },
    { title: 'Ramp Type', dataIndex: 'rampType', key: 'rampType', width: 150 },
    { title: 'Week 1 Pattern', dataIndex: 'week1', key: 'week1', width: 150 },
    { title: 'Week 2 Pattern', dataIndex: 'week2', key: 'week2', width: 150 },
    { title: 'Week 3 Pattern', dataIndex: 'week3', key: 'week3', width: 150 },
    { title: 'Week 4 Pattern', dataIndex: 'week4', key: 'week4', width: 150 },
    { title: 'Week 5 Pattern', dataIndex: 'week5', key: 'week5', width: 150 },
    { title: 'Week 6 Pattern', dataIndex: 'week6', key: 'week6', width: 150 },
    { title: 'Week 7 Pattern', dataIndex: 'week7', key: 'week7', width: 150 },
    { title: 'Week 8 Pattern', dataIndex: 'week8', key: 'week8', width: 150 },
    { title: 'Actions', dataIndex: 'actions', key: 'actions', width: 80 },
  ];

  const assignmentColumns = [
    { title: 'Ramp Name', dataIndex: 'RampName', key: 'RampName' },
    { title: 'Ramp Type', dataIndex: 'RampType', key: 'RampType' },
    { title: 'Provider', dataIndex: 'Provider', key: 'Provider' },
    { title: 'Location', dataIndex: 'Location', key: 'Location' },
    { title: 'Start Date', dataIndex: 'StartDate', key: 'StartDate' },
    { title: 'Status', dataIndex: 'Status', key: 'Status' },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => {
        // only show menu for pending assignments
        if ((record?.Status || '').toString().toUpperCase() !== 'PENDING') return null;

        const onEdit = (e: any) => {
          e && e.preventDefault && e.preventDefault();
          e && e.stopPropagation && e.stopPropagation();
          dispatch(openAssignmentModal(record));
        };

        const onDelete = (e: any) => {
          e && e.preventDefault && e.preventDefault();
          e && e.stopPropagation && e.stopPropagation();
          setDeleteRecord(record);
          setShowDeleteConfirm(true);
        };

        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip placement='top' title='Edit'>
              <Button
                style={{
                  backgroundColor: '#003366',
                  border: 'none',
                  borderRadius: '5px',
                  width: '24px',
                  height: '25px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                icon={<EditOutlined sx={{ color: 'white', fontSize: 20, marginLeft: '10px', marginRight: '10px' }} />}
                onClick={onEdit}
              />
            </Tooltip>
            <Tooltip placement='top' title='Delete'>
              <Button
                style={{
                  backgroundColor: '#dc3545',
                  border: 'none',
                  borderRadius: '5px',
                  width: '24px',
                  height: '25px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                icon={<DeleteOutlined sx={{ color: 'white', fontSize: 20, marginLeft: '10px', marginRight: '10px' }} />}
                onClick={onDelete}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className={styles.headerBar}>
        <SearchRamps
          rampTypes={rampTypes}
          defaultName={nameFilter}
          defaultTypes={typesFilter}
          onSearch={(filters) => {
            setNameFilter(filters.name || '');
            setTypesFilter(filters.types || []);
            // if user is searching while on the assignment tab, reset assignment page and ensure assignments will be fetched
            if (activeTab === 'assignment') {
              setAssignSearch((s) => ({ ...s, page: 0 }));
              setVisited((v) => ({ ...v, assignment: true }));
            }
          }}
        />
      </div>

      <div>
        {activeTab === 'assignment' ? (
          <BaseGrid
            id='rampAssignments'
            total={assignmentsTotal}
            search={assignSearch}
            setSearch={(p) => setAssignSearch((s) => ({ ...s, ...p }))}
            loading={!!assignmentsLoading}
            data={assignments}
            columns={assignmentColumns}
            rowKey={'RampAssignmentId'}
            paginationLeftExtra={paginationLeft}
            paginationExtra={paginationRight}
            isPagination={false}
            tableScroll={{ x: '100%', y: 'calc(100vh - 16em)' }}
          />
        ) : (
          <BaseGrid
            id='rampDefinitions'
            total={filteredDefinitions.length}
            search={defSearch}
            setSearch={(p) => setDefSearch((s) => ({ ...s, ...p }))}
            loading={!!rampsLoading}
            data={filteredDefinitions}
            columns={definitionColumns}
            rowKey={'key'}
            isPagination={false}
            hidePaginationControls={true}
            tableScroll={{ x: '100%', y: 'calc(100vh - 17.10em)' }}
            paginationLeftExtra={paginationLeft}
            paginationExtra={paginationRight}
          />
        )}
        <Modal
          open={showConfirm}
          centered
          title='Apply Ramp Assignment Confirmation'
          onCancel={handleConfirmNo}
          footer={[
            <Button key='no' onClick={handleConfirmNo} disabled={applyLoading}>
              No
            </Button>,
            <Button key='yes' type='primary' onClick={handleConfirmYes} loading={applyLoading}>
              Yes
            </Button>,
          ]}
          maskClosable={!applyLoading}
          closable={!applyLoading}
        >
          {applyLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
              <Spin />
            </div>
          ) : (
            <>
              Are you sure to apply this ramp assignment for <b>{selectedRecord?.Provider || ''}</b>?
            </>
          )}
        </Modal>
        <StyledConfirmModal
          title={
            <>
              <ExclamationCircleOutlined /> <span className={'confirm-title'}>Confirmation</span>
            </>
          }
          open={showDeleteConfirm}
          destroyOnClose
          centered
          footer={
            <>
              <Button
                type={'default'}
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteRecord(null);
                }}
              >
                NO
              </Button>
              <Button
                type={'primary'}
                onClick={() => {
                  if (deleteRecord) {
                    dispatch(deleteAssignment({ rampAssignmentId: deleteRecord.RampAssignmentId }));
                  }
                  setShowDeleteConfirm(false);
                  setDeleteRecord(null);
                }}
              >
                YES
              </Button>
            </>
          }
        >
          <span>
            Are you sure you want to delete the ramp assignment for{' '}
            <strong>{deleteRecord?.Provider || 'this provider'}</strong>?
          </span>
        </StyledConfirmModal>
      </div>
    </>
  );
}

const StyledConfirmModal = styled(Modal)`
  .ant-modal-content {
    width: initial;
    border-radius: 2px;

    .ant-modal-close {
      display: none;
    }

    .ant-modal-header {
      padding: 16px 24px;
      background: #fff;
      border-bottom: 1px solid #f0f0f0;
      border-radius: 2px;

      .ant-modal-title {
        font-size: 15px;
        color: #ff991f;

        .confirm-title {
          color: #000;
          margin-left: 5px;
        }
      }
    }
  }
`;
